import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";
import DataService from "features/Application/services/dataServices/typedDataService";
import {
  type PerformanceWithAccountFilter,
  lineChartFactory,
  getFormattedTimeStringFromPeriod,
} from "../../../../Library/Common/utils/performanceUtils";
import { type PaginationParams } from "features/Library/Common/models";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type ColumnChartProps, type LinechartProps } from "../../../../../components/charts";
import { type ColumnDataPoint } from "../../../../../components/charts/types/ColumnChart";
import {
  type CustomerEnrollmentsStatisticsResponse,
  type PrioritizedFlowTableTemplates,
} from "../../DataService/types";
import { CHART_PERIOD, type ChartPeriod } from "../../../../../hooks/useChartPeriodMeasure";
import { type GroupActivityMetrics } from "../../../Groups/EditGroup/Performance/types";
import { type BarChartData } from "../../../../../components/charts/types/Shared";
import moment from "moment";
import { SERVER_DATE_ONLY_FORMAT } from "../../../../../utils/dateTimeUtils";
import { type DataPoint } from "../../../../../components/charts/types/HorizontalBarChart";
import { starts, completes } from "../../../../Library/Common/utils/performanceUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../../../../../components/cards/CardReporting/CardReporting.module.scss";
import { faSquareArrowDown, faSquareExclamation, faSquareArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type FetchPageFn } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const customerEnrolledStatisticsColorRange = [
  reportingColors.yellow,
  reportingColors.greenDark,
  reportingColors.red,
];

type TemplateQueryFunctionContext = QueryFunctionContext<
  [_name: string, templateId: number, filter: PerformanceWithAccountFilter, chartPeriod?: ChartPeriod]
>;

export const getTemplateCustomerEnrolledLineChartQuery = async ({
  queryKey,
}: TemplateQueryFunctionContext): Promise<{
  lineData: { xData: LinechartProps["xData"]; yData: LinechartProps["yData"] };
  barData: {
    data: ColumnChartProps["data"];
    xAxisTickFormat: ColumnChartProps["xAxisTickFormat"];
    domain: [number, number];
  };
}> => {
  const { data } = await DataService.groupTemplates.getTemplateCustomerEnrollmentsLineChart(queryKey[1], queryKey[2]);
  const lineData = lineChartFactory(data);
  const barData = data.map<ColumnDataPoint>((item) => ({
    id: item.Date,
    category: item.Date,
    value: item.Enrollments,
    fillColor: reportingColors.blue,
  }));

  return {
    lineData: { xData: [lineData.Date], yData: [lineData.Enrollments] },
    barData: {
      data: barData,
      xAxisTickFormat: (d) =>
        getFormattedTimeStringFromPeriod(d.toLocaleString(), CHART_PERIOD.DAY, [
          new Date(queryKey[2].dateFrom),
          new Date(queryKey[2].dateTo),
        ]),
      domain: [0, Math.max(...barData.map((item) => item.value))],
    },
  };
};

export const getTemplateEnrollmentLineChartQuery = async ({ queryKey }: TemplateQueryFunctionContext) => {
  const { data } = await DataService.groupTemplates.getTemplateEnrollmentLineChart(queryKey[1], queryKey[2]);
  const lineData = lineChartFactory(data);
  return {
    lineData: {
      xData: [lineData.Date, lineData.Date, lineData.Date],
      yData: [lineData.TotalUsers, lineData.AddedUsers, lineData.DroppedUsers],
    },
  };
};

export const getTemplateCustomerEnrolledStatisticsQuery = async ({
  queryKey,
}: TemplateQueryFunctionContext): Promise<CustomerEnrollmentsStatisticsResponse> => {
  const { data } = await DataService.groupTemplates.getTemplateCustomerEnrollmentsStatistics(queryKey[1], queryKey[2]);
  return data;
};

export const getTemplateSummaryQuery = async ({ queryKey }: TemplateQueryFunctionContext) => {
  const { data } = await DataService.groupTemplates.getTemplateCustomerEnrollmentsSummary(queryKey[1], queryKey[2]);
  return data;
};

export const useCustomersEngagementQuery = (templateId: number, filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["templateCustomerEngagement", templateId, filter],
    queryFn: getTemplateCustomerEnrolledStatisticsQuery,
    enabled: filter.type !== undefined,
  });
};

export const getMostActiveCustomersQuery = async ({ queryKey }: TemplateQueryFunctionContext) =>
  (await DataService.groupTemplates.getMostActiveCustomers(queryKey[1], queryKey[2])).data;

export const getTemplateActivityLineChartQuery = async ({ queryKey }: TemplateQueryFunctionContext) => {
  const { data } = await DataService.groupTemplates.getTemplateActivityData(queryKey[1], queryKey[2]);
  const lineData = lineChartFactory(data);
  const barData: BarChartData<GroupActivityMetrics>[] = data.map((item) => ({
    group: moment(item.Date).format(SERVER_DATE_ONLY_FORMAT),
    TotalInteractions: item.TotalInteractions,
    TotalOutreach: item.TotalOutreach,
  }));
  const order: (keyof GroupActivityMetrics)[] = ["TotalOutreach", "TotalInteractions"];

  return {
    lineData: { xData: [lineData.Date, lineData.Date], yData: [lineData.TotalOutreach, lineData.TotalInteractions] },
    barData,
    order,
  };
};

export const getTemplateCardReportingQuery = async ({
  queryKey,
}: TemplateQueryFunctionContext): Promise<CardReportingItem[]> => {
  const { data } = await DataService.groupTemplates.getTemplateUserActivityCardData(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Platform Outreach",
      stat: data.TotalOutreach,
      popupBody:
        "Total number of outreach attempts, emails, messages, etc. to engage users during the specified time period.",
    },
    {
      statDescription: "Platform Interactions",
      stat: data.TotalInteractions,
      popupBody:
        "Total number of user interactions with the platform, emails, messages, etc. during the specified time period.",
    },
  ];
};

export const getTemplateEnrollmentCardDataQuery = async ({
  queryKey,
}: TemplateQueryFunctionContext): Promise<CardReportingItem[]> => {
  const { data } = await DataService.groupTemplates.getTemplateEnrollmentCardData(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Currently Enrolled",
      stat: data.TotalUsers,
      popupBody: "Total users currently enrolled in this template at the end of the specified time period.",
    },
    {
      statDescription: "Dropped",
      stat: data.DroppedUsers,
      popupBody: "Total users dropped from this template during the specified time period.",
    },
    {
      statDescription: "Drop Rate",
      stat: data.DropRate * 100,
      isStatPercentage: true,
      popupBody:
        "Percentage of users who dropped from the template based on enrollment criteria during the specified time period.",
    },
  ];
};

export const getTemplateFlowEngagementChart = async ({ queryKey }: TemplateQueryFunctionContext) => {
  const { data } = await DataService.groupTemplates.getTemplateFlowEngagementResponse(queryKey[1], queryKey[2]);

  const barData: DataPoint[] = [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: data.TotalStartedFlows,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: data.TotalCompletedFlows,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Active Users",
      stat: data.TotalUsers,
      popupBody: "Total active users in all Flows for this template during the selected period.",
    },
  ];

  return {
    barData,
    cards,
  };
};

export const getTemplateEmailEngagement = async ({ queryKey }: TemplateQueryFunctionContext) => {
  const { data } = await DataService.groupTemplates.getTemplateEmailEngagement(queryKey[1], queryKey[2]);

  const barData = [
    {
      id: "sends",
      category: "Sends",
      value: data.TotalEmailSends,
      fillColor: reportingColors.blue,
    },
    {
      id: "opens",
      category: "Opens",
      value: data.TotalEmailOpens,
      fillColor: reportingColors.greenDark,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.TotalEmailClicks,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Email Sends",
      stat: data.TotalEmailSends,
      popupBody: "The total number of email sends during the specified time period.",
    },
    {
      statDescription: "Email Opens",
      stat: data.TotalEmailOpens,
      popupBody: "The total number of emails opened during the specified time period.",
    },
    {
      statDescription: "Email Open Rate",
      stat: data.EmailOpenRate * 100,
      popupBody: "The percentage of emails opened compared to the total number of emails sent.",
      isStatPercentage: true,
    },
    {
      statDescription: "Email CTR",
      stat: data.ClickThroughRate * 100,
      popupBody: "The percentage of emails clicked compared to the total number of emails sent.",
      isStatPercentage: true,
    },
  ];

  return { cards, barData };
};

export const getTemplatePrioritizedFlowCards = async ({
  queryKey,
}: TemplateQueryFunctionContext): Promise<CardReportingItem[]> => {
  const { data } = await DataService.groupTemplates.getTemplatePrioritizedFlowCards(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Prioritized Flows",
      stat: data.TotalPrioritizedFlows,
      popupBody: "The total number of Flows prioritized.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareExclamation} />
          <div>Required</div>
        </div>
      ),
      key: "requiredFlows",
      stat: data.TotalRequiredFlows,
      popupBody: "The total number of Flows with required priority.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowUp} />
          <div>Important</div>
        </div>
      ),
      key: "importantFlows",
      stat: data.TotalImportantFlows,
      popupBody: "The total number of Flows with important priority.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowDown} />
          <div>Recommended</div>
        </div>
      ),
      key: "recommendedFlows",
      stat: data.TotalRecommendedFlows,
      popupBody: "The total number of Flows with recommended priority.",
    },
  ];
};

type TemplatePrioritizedFlowRequest = PerformanceWithAccountFilter & PaginationParams;

export const createTemplatePrioritizedFlowFetcher = (
  templateId: number,
  filter: PerformanceWithAccountFilter,
): FetchPageFn<PrioritizedFlowTableTemplates> => {
  return async (pagination, signal) => {
    const params: TemplatePrioritizedFlowRequest = {
      ...filter,
      skip: pagination.skip,
      top: pagination.top,
      sortBy: pagination.sortBy,
      sortOrder: pagination.sortOrder,
    };

    const response = await DataService.groupTemplates.getTemplatePrioritizedFlowTable(templateId, params, signal);

    return {
      rows: response.data,
      totalCount: Number.parseInt(response.headers[countHeaderName]),
    };
  };
};
